import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { PromoBanner } from "@/components/layout/PromoBanner";
import { Hero } from "@/components/home/Hero";
import { WhyUs } from "@/components/home/WhyUs";
import { Delivery } from "@/components/home/Delivery";
import { Testimonials } from "@/components/home/Testimonials";
import { Blog } from "@/components/home/Blog";
import { FAQ } from "@/components/home/FAQ";
import { Contact } from "@/components/home/Contact";
import { FloatingCart } from "@/components/home/FloatingCart";
import { TripSetup, StickyTripBar } from "@/components/booking/TripSetup";
import { CategoryBrowser } from "@/components/booking/CategoryBrowser";
import { AddonPrompt } from "@/components/booking/AddonPrompt";
import { BookingWidget } from "@/components/booking/BookingWidget";
import { OutOfStockModal } from "@/components/booking/OutOfStockModal";
import { StockRequestModal } from "@/components/booking/StockRequestModal";
import { API } from "@/config";
import { useAuth } from "@/Admin";

const STORAGE_KEY = "egr_home_trip_v1";
// Probe quantity for the catalog availability check — large enough that any
// product with fewer free units shows up in `shortages` with its real count.
const STOCK_PROBE_QTY = 99;

const capMessage = (n) => n <= 0
  ? "This item is sold out for your dates."
  : `Only ${n} of these ${n === 1 ? "is" : "are"} available for your dates — you've already got ${n === 1 ? "it" : "all of them"} in your cart.`;

const loadPersisted = () => {
  try {
    // sessionStorage stores non-sensitive booking intent only (cart items, dates,
    // resort names). No PII, no payment data. Cleared automatically on tab close.
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return {
      cart: p.cart || {},
      cartOptions: p.cartOptions || {},
      cartAddons: p.cartAddons || {},
      range: {
        from: p.range?.from ? new Date(p.range.from) : undefined,
        to: p.range?.to ? new Date(p.range.to) : undefined,
      },
      isOffProperty: !!p.isOffProperty,
      zoneId: p.zoneId || "",
      resort: p.resort || "",
      address: p.address || "",
      deliverySlot: p.deliverySlot || "",
      pickupSlot: p.pickupSlot || "",
      disneyAvailability: !!p.disneyAvailability,
      differentPickup: !!p.differentPickup,
      pickupIsOffProperty: !!p.pickupIsOffProperty,
      pickupZoneId: p.pickupZoneId || "",
      pickupResort: p.pickupResort || "",
      pickupAddress: p.pickupAddress || "",
      addrCheck: p.addrCheck || null,
      pickupAddrCheck: p.pickupAddrCheck || null,
    };
    } catch (e) { console.warn("egr_home_trip parse failed:", e?.message); return null; }
};

export const Home = () => {
  const { user } = useAuth();
  const persisted = useRef(loadPersisted()).current;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resorts, setResorts] = useState([]);
  const [blog, setBlog] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cart, setCart] = useState(persisted?.cart || {});
  // Per-product variation selections. Shape: { [product_id]: string[] }
  // where the array holds the chosen ProductOption ids (one per group).
  // Kept as a sibling of `cart` (rather than merged) so any existing code
  // that reads `cart[pid]` as a number keeps working. When items are sent
  // to /availability, /quote, /checkout, we inject the option_ids inline.
  const [cartOptions, setCartOptions] = useState(persisted?.cartOptions || {});
  // addon product id -> parent product id (the item it was offered with)
  const [cartAddons, setCartAddons] = useState(persisted?.cartAddons || {});
  const [addonPrompt, setAddonPrompt] = useState(null);
  const [zones, setZones] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [byob, setByob] = useState(null);
  // Trip state — shared across TripSetup + CategoryBrowser + BookingWidget + StickyTripBar.
  const [range, setRange] = useState(persisted?.range || { from: undefined, to: undefined });
  const [isOffProperty, setIsOffProperty] = useState(persisted?.isOffProperty || false);
  const [zoneId, setZoneId] = useState(persisted?.zoneId || "");
  const [resort, setResort] = useState(persisted?.resort || "");
  const [address, setAddress] = useState(persisted?.address || "");
  const [addrCheck, setAddrCheck] = useState(persisted?.addrCheck || null);
  const [deliverySlot, setDeliverySlot] = useState(persisted?.deliverySlot || "");
  const [pickupSlot, setPickupSlot] = useState(persisted?.pickupSlot || "");
  const [disneyAvailability, setDisneyAvailability] = useState(persisted?.disneyAvailability || false);
  // Optional split-location pickup — customer opted in via TripSetup modal.
  const [differentPickup, setDifferentPickup] = useState(persisted?.differentPickup || false);
  const [pickupIsOffProperty, setPickupIsOffProperty] = useState(persisted?.pickupIsOffProperty || false);
  const [pickupZoneId, setPickupZoneId] = useState(persisted?.pickupZoneId || "");
  const [pickupResort, setPickupResort] = useState(persisted?.pickupResort || "");
  const [pickupAddress, setPickupAddress] = useState(persisted?.pickupAddress || "");
  const [pickupAddrCheck, setPickupAddrCheck] = useState(persisted?.pickupAddrCheck || null);
  const [tripSetupOpen, setTripSetupOpen] = useState(false);
  const [oos, setOos] = useState({ open: false, product: null, alternatives: [] });
  // Products that are sold out for the chosen trip dates — computed after the
  // customer picks a range so the catalog only shows what's actually bookable.
  const [unavailableIds, setUnavailableIds] = useState(() => new Set());
  // product_id -> units still free for the chosen dates (only ids with a cap; missing = plenty)
  const [stockMap, setStockMap] = useState(() => new Map());
  const [stockRequest, setStockRequest] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Persist cart + trip state so a Back-navigation from /checkout restores everything.
  // Stores non-sensitive booking intent (items, dates, resort names). No PII.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        cart,
        cartOptions,
        cartAddons,
        range: {
          from: range?.from ? range.from.toISOString() : null,
          to: range?.to ? range.to.toISOString() : null,
        },
        isOffProperty, zoneId, resort, address,
        deliverySlot, pickupSlot, disneyAvailability,
        differentPickup, pickupIsOffProperty, pickupZoneId, pickupResort, pickupAddress,
        addrCheck, pickupAddrCheck,
      }));
    } catch (e) { console.warn("egr_home_trip persist failed:", e?.message); }
  }, [cart, cartOptions, cartAddons, range, isOffProperty, zoneId, resort, address, deliverySlot, pickupSlot,
    disneyAvailability, differentPickup, pickupIsOffProperty, pickupZoneId, pickupResort, pickupAddress,
    addrCheck, pickupAddrCheck]);

  // An add-on only lives in the cart while its parent item does.
  useEffect(() => {
    const orphaned = Object.entries(cartAddons).filter(([, parentId]) => !(cart[parentId] > 0));
    if (orphaned.length === 0) return;
    setCart((c) => { const n = { ...c }; orphaned.forEach(([aid]) => { delete n[aid]; }); return n; });
    setCartAddons((c) => { const n = { ...c }; orphaned.forEach(([aid]) => { delete n[aid]; }); return n; });
  }, [cart, cartAddons]);

  // Scroll to the section hash when arriving from another route (e.g. /deals → /#catalog).
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      if (hash === "#top") window.scrollTo({ top: 0, behavior: "smooth" });
      else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [hash]);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products || []));
    axios.get(`${API}/categories`).then((r) => setCategories(r.data.categories || []));
    axios.get(`${API}/resorts`).then((r) => setResorts(r.data.resorts || []));
    axios.get(`${API}/blog`).then((r) => setBlog(r.data.posts || []));
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data.testimonials || []));
    axios.get(`${API}/faqs`).then((r) => setFaqs(r.data.faqs || []));
    axios.get(`${API}/time-slots`).then((r) => setTimeSlots(r.data.slots || []));
    axios.get(`${API}/settings`).then((r) => {
      const d = r.data || {};
      setByob({ enabled: !!d.byob_enabled, percent: Number(d.byob_percent) || 0, minUnits: Number(d.byob_min_units) || 3 });
    });
    axios.get(`${API}/zones`).then((r) => {
      const zs = r.data.zones || [];
      setZones(zs);
      const wdw = zs.find((z) => z.id === "wdw-core");
      if (wdw) setZoneId((prev) => prev || wdw.id);
    });
  }, []);

  useEffect(() => {
    if (user && user.role === "customer" && user.address && !address) {
      setAddress(user.address);
    }
    // eslint-disable-next-line
  }, [user]);

  // Re-check availability whenever the date range or product list changes.
  // Uses the same /availability endpoint the addToCart flow uses so answers stay
  // consistent with what the server would enforce at checkout.
  useEffect(() => {
    if (!range?.from || !range?.to || products.length === 0) {
      setUnavailableIds(new Set());
      setStockMap(new Map());
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    (async () => {
      try {
        const start = format(range.from, "yyyy-MM-dd");
        const end = format(range.to, "yyyy-MM-dd");
        const res = await axios.post(`${API}/availability`, {
          start_date: start, end_date: end,
          items: products.map((p) => ({ product_id: p.id, quantity: STOCK_PROBE_QTY })),
        });
        if (cancelled) return;
        const map = new Map();
        (res.data.shortages || []).forEach((s) => {
          const free = Number(s.bundle_units_available ?? s.available ?? 0);
          map.set(s.product_id, Math.min(free, map.has(s.product_id) ? map.get(s.product_id) : free));
        });
        setStockMap(map);
        setUnavailableIds(new Set([...map.entries()].filter(([, n]) => n <= 0).map(([id]) => id)));
      } catch {
        if (!cancelled) { setUnavailableIds(new Set()); setStockMap(new Map()); }
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range?.from, range?.to, products]);

  const saveTrip = ({
    range: r, isOffProperty: iop, zoneId: z, resort: rs, address: a, addrCheck: ac,
    deliverySlot: ds, pickupSlot: ps, disneyAvailability: da,
    differentPickup: dp, pickupIsOffProperty: piop, pickupZoneId: pz, pickupResort: prs, pickupAddress: pa, pickupAddrCheck: pac,
  }) => {
    setRange(r);
    latestRangeRef.current = r;
    setIsOffProperty(iop);
    setZoneId(z);
    setResort(rs);
    setAddress(a);
    setAddrCheck(ac);
    setDeliverySlot(ds || "");
    setPickupSlot(ps || "");
    setDisneyAvailability(!!da);
    setDifferentPickup(!!dp);
    setPickupIsOffProperty(!!piop);
    setPickupZoneId(pz || "");
    setPickupResort(prs || "");
    setPickupAddress(pa || "");
    setPickupAddrCheck(pac || null);
    // After TripSetup: if user came from a product page, open that product
    if (pendingFocusRef.current) {
      setCatalogFocusId(pendingFocusRef.current);
      pendingFocusRef.current = null;
    }
  };

  // A shopper tried to add an item before picking dates — remember it so we
  // can finish the add automatically once Trip Setup is saved.
  const pendingAddRef = useRef(null);
  const latestRangeRef = useRef(range);
  useEffect(() => { latestRangeRef.current = range; }, [range]);

  // When arriving from a product page via ?focus=<id>, defer opening the
  // product in the catalog until AFTER TripSetup is confirmed.
  const pendingFocusRef = useRef(null);
  const [catalogFocusId, setCatalogFocusId] = useState(null);
  const focusReadRef = useRef(false);
  useEffect(() => {
    if (focusReadRef.current) return;
    focusReadRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const fid = params.get("focus");
    if (!fid) return;
    // Clean the URL immediately so bookmarks/shares look clean
    window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    const alreadyHasDates = latestRangeRef.current?.from && latestRangeRef.current?.to;
    if (alreadyHasDates) {
      // Dates are already set — open the product right away
      setCatalogFocusId(fid);
    } else {
      // Store for after TripSetup is saved
      pendingFocusRef.current = fid;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToCart = async (id, opts = {}) => {
    if (!range.from || !range.to) {
      pendingAddRef.current = { id, opts };
      toast("Pick your dates first — we'll add it to your cart right after.", { icon: "📅" });
      setTripSetupOpen(true);
      return;
    }
    // First time this item goes in the cart: offer its linked add-ons (snack tray etc.).
    const parentProduct = products.find((p) => p.id === id);
    const linkedAddons = (parentProduct?.addon_product_ids || [])
      .map((aid) => products.find((p) => p.id === aid))
      .filter(Boolean);
    if (!opts.addons_resolved && linkedAddons.length > 0 && !(cart[id] > 0)) {
      setAddonPrompt({ product: parentProduct, addons: linkedAddons, opts });
      return;
    }
    const chosenAddonIds = Array.isArray(opts.addon_ids) ? opts.addon_ids : [];
    const nextOptionIds = Array.isArray(opts.option_ids) ? opts.option_ids : (cartOptions[id] || []);
    const nextQty = (cart[id] || 0) + 1;
    if (stockMap.has(id) && nextQty > stockMap.get(id) && (cart[id] || 0) > 0) {
      toast.error(capMessage(stockMap.get(id)));
      return;
    }
    const start = format(range.from, "yyyy-MM-dd");
    const end = format(range.to, "yyyy-MM-dd");
    try {
      const res = await axios.post(`${API}/availability`, {
        start_date: start, end_date: end,
        items: [
          { product_id: id, quantity: nextQty, option_ids: nextOptionIds },
          ...chosenAddonIds.map((aid) => ({ product_id: aid, quantity: (cart[aid] || 0) + 1, addon_for: id })),
        ],
      });
      const shortIds = new Set((res.data.shortages || []).map((s) => s.product_id));
      if (res.data.available || !shortIds.has(id)) {
        const okAddons = chosenAddonIds.filter((aid) => !shortIds.has(aid));
        setCart((c) => {
          const n = { ...c, [id]: nextQty };
          okAddons.forEach((aid) => { n[aid] = (c[aid] || 0) + 1; });
          return n;
        });
        if (okAddons.length > 0) setCartAddons((c) => ({ ...c, ...Object.fromEntries(okAddons.map((aid) => [aid, id])) }));
        if (nextOptionIds.length > 0) {
          setCartOptions((c) => ({ ...c, [id]: nextOptionIds }));
        }
        const missed = chosenAddonIds.filter((aid) => shortIds.has(aid)).map((aid) => products.find((p) => p.id === aid)?.name).filter(Boolean);
        if (missed.length > 0) toast.error(`${missed.join(", ")} isn't available for your dates — added the ${parentProduct?.name || "item"} on its own.`);
        else toast.success(okAddons.length > 0 ? "Added with your extras ✨" : "Added to your enchanted booking ✨");
        return;
      }
      // Already holding some units — the server just told us there are no more.
      if ((cart[id] || 0) > 0) {
        const row = (res.data.shortages || []).find((s) => s.product_id === id);
        const free = Number(row?.bundle_units_available ?? row?.available ?? cart[id]);
        toast.error(capMessage(free));
        return;
      }
      const all = await axios.post(`${API}/availability`, {
        start_date: start, end_date: end,
        items: products.map((p) => ({ product_id: p.id, quantity: 1 })),
      });
      const allShort = new Set((all.data.shortages || []).map((s) => s.product_id));
      const alternatives = products.filter((p) => p.id !== id && !p.addon_only && !allShort.has(p.id));
      const unavailableProduct = products.find((p) => p.id === id);
      setOos({ open: true, product: unavailableProduct, alternatives });
    } catch (e) {
      toast.error("Couldn't verify stock right now — please try again.");
    }
  };

  // Finish a pending add once the shopper saves their dates in Trip Setup.
  useEffect(() => {
    if (!range?.from || !range?.to) return;
    const pending = pendingAddRef.current;
    if (!pending) return;
    pendingAddRef.current = null;
    addToCart(pending.id, pending.opts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.from, range?.to]);

  return (
    <>
      {faqs.length > 0 && (
        <PageSEO
          title="Disney World Stroller Rentals | Enchanted Gear Rentals"
          description="Rent clean, premium single and double strollers near Walt Disney World and Orlando. Free delivery to Disney resorts, Universal hotels, vacation homes and Airbnb."
          canonical={SITE_URL}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q || f.question,
              acceptedAnswer: { "@type": "Answer", text: f.a || f.answer },
            })),
          }}
        />
      )}
      <PromoBanner />
      <Nav />
      <Hero />
      <WhyUs />
      {addonPrompt && (
        <AddonPrompt
          product={addonPrompt.product}
          addons={addonPrompt.addons}
          range={range}
          onClose={() => setAddonPrompt(null)}
          onConfirm={(addonIds) => {
            const { product, opts } = addonPrompt;
            setAddonPrompt(null);
            addToCart(product.id, { ...opts, addons_resolved: true, addon_ids: addonIds });
          }}
        />
      )}
      <TripSetup
        range={range}
        isOffProperty={isOffProperty}
        zoneId={zoneId}
        resort={resort}
        address={address}
        addrCheck={addrCheck}
        deliverySlot={deliverySlot}
        pickupSlot={pickupSlot}
        disneyAvailability={disneyAvailability}
        differentPickup={differentPickup}
        pickupIsOffProperty={pickupIsOffProperty}
        pickupZoneId={pickupZoneId}
        pickupResort={pickupResort}
        pickupAddress={pickupAddress}
        pickupAddrCheck={pickupAddrCheck}
        zones={zones}
        timeSlots={timeSlots}
        onSave={saveTrip}
        externalOpen={tripSetupOpen}
        onExternalOpenChange={(open) => {
          setTripSetupOpen(open);
          // Trip Setup dismissed without dates — drop any pending cart add.
          // latestRangeRef is synced synchronously in saveTrip, so a close
          // right after a successful confirm keeps the pending add alive.
          const r = latestRangeRef.current;
          if (!open && (!r?.from || !r?.to)) pendingAddRef.current = null;
        }}
      />
      <CategoryBrowser
        products={products}
        categories={categories}
        cart={cart}
        cartOptions={cartOptions}
        addToCart={addToCart}
        range={range}
        unavailableIds={unavailableIds}
        stockMap={stockMap}
        availabilityLoading={availabilityLoading}
        onEditTrip={() => setTripSetupOpen(true)}
        onRequestStock={(p) => setStockRequest(p)}
        byob={byob}
        focusProductId={catalogFocusId}
        onFocusProductConsumed={() => setCatalogFocusId(null)}
      />

      <BookingWidget
        products={products} cart={cart} setCart={setCart} addToCart={addToCart}
        cartOptions={cartOptions} setCartOptions={setCartOptions}
        range={range}
        isOffProperty={isOffProperty}
        zoneId={zoneId}
        resort={resort}
        address={address}
        addrCheck={addrCheck}
        deliverySlot={deliverySlot}
        pickupSlot={pickupSlot}
        differentPickup={differentPickup}
        pickupIsOffProperty={pickupIsOffProperty}
        pickupZoneId={pickupZoneId}
        pickupResort={pickupResort}
        pickupAddress={pickupAddress}
        pickupAddrCheck={pickupAddrCheck}
        zones={zones}
        onEditTrip={() => setTripSetupOpen(true)}
      />
      <Delivery resorts={resorts} />
      <Testimonials items={testimonials} />
      <Blog posts={blog} />
      <FAQ faqs={faqs} />
      <Contact />
      <Footer />

      {stockRequest && (
        <StockRequestModal product={stockRequest} range={range} onClose={() => setStockRequest(null)} />
      )}
      <OutOfStockModal
        open={oos.open}
        onClose={() => setOos({ open: false, product: null, alternatives: [] })}
        unavailableProduct={oos.product}
        alternatives={oos.alternatives}
        onSelect={(altId) => { setOos({ open: false, product: null, alternatives: [] }); addToCart(altId); }}
        cart={cart}
        range={range}
      />
      <FloatingCart
        cart={cart} setCart={setCart} products={products} range={range} zones={zones}
        onIncrement={(id) => addToCart(id)}
        cartOptions={cartOptions}
        cartAddons={cartAddons}
        zoneId={zoneId} resort={resort} address={address} addrCheck={addrCheck}
        isOffProperty={isOffProperty}
        deliverySlot={deliverySlot} pickupSlot={pickupSlot}
        differentPickup={differentPickup}
        pickupIsOffProperty={pickupIsOffProperty}
        pickupZoneId={pickupZoneId}
        pickupResort={pickupResort}
        pickupAddress={pickupAddress}
        onEditTrip={() => setTripSetupOpen(true)}
        byob={byob}
      />

      <StickyTripBar
        range={range}
        isOffProperty={isOffProperty}
        zoneId={zoneId}
        resort={resort}
        address={address}
        deliverySlot={deliverySlot}
        pickupSlot={pickupSlot}
        zones={zones}
        timeSlots={timeSlots}
        onEditTrip={() => setTripSetupOpen(true)}
      />
    </>
  );
};
